import React, {useMemo, useState, useCallback, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';
import {FaIcon} from "../../FaIcon";
import {ActiveColumnsList} from "../ActiveColumnsList";
import {getColumnData} from "../columns";
import {CATEGORY_CUSTOM, channelGroupForMetric, channelGroupForColumn} from "../columns_metadata";
import {countActiveChips, ROW_TYPE_FIXED} from "../columns_stacked_rows";
import {ChannelBadge} from "../ChannelBadge";
import {getColumnHelp} from "../columns_descriptions";
import {isCustomMetricsEnabled} from "../../../utils/FeatureFlags";
import {PeriodsSelect} from "../../elements/PeriodsSelect";
import {ALL_PERIODS, getPropertiesForPeriod, PERIOD_THIS_PERIOD} from "../../../periods";
import {anchorAttr} from "../../../guides/anchors";

const MAX_COMPARATIVES = 12;
const COMPARATIVE_EDITOR_PATH = '/agency/personalization/columns';

const CATEGORY_LABEL = (t) => ({
    GOALS: t('config_columns_modal.categories.goals', 'Objetivos'),
    BUDGET: t('config_columns_modal.categories.budget', 'Presupuesto'),
    PERFORMANCE: t('config_columns_modal.categories.performance', 'Performance'),
    CONVERSIONS: t('config_columns_modal.categories.conversions', 'Conversiones'),
    VIDEO: t('config_columns_modal.categories.video', 'Video'),
    SOCIAL: t('config_columns_modal.categories.social', 'Social'),
    FUNDS: t('config_columns_modal.categories.funds', 'Saldos'),
    ATTRIBUTES: t('config_columns_modal.categories.attributes', 'Atributos'),
    STATUS: t('config_columns_modal.categories.status', 'Estado'),
    CUSTOM: t('config_columns_modal.categories.custom', 'Métricas personalizadas'),
});

// Sólo icons que están registrados en src/utils/init_icons_library.js (FA7 subset cargado)
const CATEGORY_ICON = {
    GOALS: 'bullseye', BUDGET: 'chart-line', PERFORMANCE: 'magnifying-glass-chart',
    CONVERSIONS: 'gauge-high', VIDEO: 'video', SOCIAL: 'thumbs-up',
    FUNDS: 'crown', ATTRIBUTES: 'tags', STATUS: 'bolt',
    CUSTOM: 'wand-magic-sparkles',
};

const CATEGORY_ORDER_NON_CUSTOM = ['GOALS', 'BUDGET', 'PERFORMANCE', 'CONVERSIONS', 'VIDEO', 'SOCIAL', 'FUNDS', 'ATTRIBUTES', 'STATUS'];

// Métricas destacadas que van arriba (después de las fijas) dentro de su categoría, en este orden
// — el resto (el "malón" de GA4/Ecommerce) queda debajo. Pedido Pablo: estas 3 son las más importantes.
const FEATURED_BY_CATEGORY = {
    CONVERSIONS: ['Conversion Rate', 'Meta_Pixel_purchase', 'Meta_Cost_per_pixel_purchase'],
};

// Orden estable dentro de una categoría: fijas (sin metricName) → destacadas (en su orden) → resto.
function sortCategoryRows(cat, rows) {
    const featured = FEATURED_BY_CATEGORY[cat];
    if (!featured) return rows;
    // Match case-insensitive: el metric_name del backend puede variar en capitalización
    // (ej. Meta_Pixel_Purchase vs Meta_Pixel_purchase) y no queremos que el orden se rompa.
    const featuredLower = featured.map((m) => m.toLowerCase());
    const rank = (row) => {
        if (!row.metricName) return -1;                 // fijas primero (mantienen su orden natural)
        const fi = featuredLower.indexOf(row.metricName.toLowerCase());
        return fi >= 0 ? fi : featured.length;          // destacadas en su orden; resto al final
    };
    return rows
        .map((row, idx) => ({row, idx}))
        .sort((a, b) => (rank(a.row) - rank(b.row)) || (a.idx - b.idx)) // estable
        .map((x) => x.row);
}

// Mapeo de filas FIXED del modal → metric_name del backend (vocabulario de flex/comparativas).
const FIXED_ROW_METRIC_NAME = {
    COST: 'Cost', CLICKS: 'Clicks', IMPRESSIONS: 'Impressions', CPC: 'CPC', CTR: 'CTR', CPM: 'CPM',
    CONVERSIONS: 'Conversions', CPA: 'CPA', PURCHASES_VALUE: 'Purchase Value', ROAS: 'ROAS', ACOS: 'ACOS',
};

function resolveMetricName(row) {
    if (!row) return null;
    if (row.metricName) return row.metricName;
    return FIXED_ROW_METRIC_NAME[row.id] || null;
}

function rowMatchesSearch(row, query) {
    if (!query) return true;
    const q = query.toLowerCase();
    if ((row.titleKey || '').toLowerCase().includes(q)) return true;
    return row.chips.some((c) => (c.label || '').toLowerCase().includes(q));
}

function rowMatchesFilters(row, filters) {
    if (filters.channel && row.channel !== filters.channel && row.channel !== 'BU') return false;
    if (filters.category && row.category !== filters.category) return false;
    return true;
}

// Tooltip por portal (position:fixed) — escapa el overflow/scroll del panel para que NO se recorte.
// El warning-tooltip absolute de Dashbo se cortaba contra el borde del panel con scroll.
function TipSpan({text, className, onClick, children}) {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const show = () => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth || 1200;
        const left = Math.min(Math.max(r.left + r.width / 2, 172), vw - 172);
        setTip({top: Math.round(r.bottom + 8), left: Math.round(left)});
    };
    const hide = () => setTip(null);

    return (
        <span
            ref={ref}
            className={className}
            onMouseEnter={show}
            onMouseLeave={hide}
            onClick={onClick}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {children}
            {tip && createPortal(
                <div className="ccm-tip" style={{top: tip.top, left: tip.left}}>{text}</div>,
                document.body
            )}
        </span>
    );
}

// Ícono "?" de ayuda contextual.
function InfoHelp({text}) {
    if (!text) return null;
    return (
        <TipSpan
            text={text}
            className="ccm-help"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
            <FaIcon iconName="question-circle" className="ccm-help__icon"/>
        </TipSpan>
    );
}

function RowBadges({row, t}) {
    // Badge de canal con logo + color de marca (GA4 / Ecommerce / Meta / Google / TikTok / LinkedIn).
    // Las métricas core de paid media / BU no llevan badge (channelGroup = null → no renderiza).
    const channelGroup = channelGroupForMetric(row.metricName, row.channel);
    return (
        <>
            {row.isFlex && (
                <span className="ccm-stacked-row__badge -flex" title={t('config_columns_modal.flex_badge_title', 'Indicador personalizado de la agencia')}>
                    <FaIcon iconName="wand-magic-sparkles"/>
                </span>
            )}
            <ChannelBadge group={channelGroup}/>
        </>
    );
}

// Builder inline: crea una comparativa (la misma métrica en otro período, con/sin comparación).
// Reusa PeriodsSelect del editor para opciones/labels idénticas. Persiste vía onCreate (agencia).
function ComparativeBuilder({metricName, count, onCreate, onClose, t}) {
    const [mainPeriod, setMainPeriod] = useState(PERIOD_THIS_PERIOD);
    const [vsPeriod, setVsPeriod] = useState('');
    const [creating, setCreating] = useState(false);

    const mainPeriods = useMemo(() => {
        const props = getPropertiesForPeriod(t);
        return ALL_PERIODS.filter((p) => props[p] && !props[p].depends_of_other_period);
    }, [t]);

    const handleCreate = () => {
        if (!mainPeriod || creating) return;
        setCreating(true);
        Promise.resolve(onCreate({metricName, mainPeriod, vsPeriod: vsPeriod || null}))
            .then(() => { setCreating(false); onClose(); })
            .catch(() => { setCreating(false); });
    };

    return (
        <div className="ccm-cmp-builder" onClick={(e) => e.stopPropagation()}>
            <div className="ccm-cmp-builder__head">
                <span className="ccm-cmp-builder__title">
                    <FaIcon iconName="exchange-alt"/>
                    {t('config_columns_modal.new_comparative', 'Nueva métrica comparativa')}
                </span>
                <span className="ccm-cmp-builder__count">{count}/{MAX_COMPARATIVES}</span>
            </div>
            <div className="ccm-cmp-builder__fields">
                <label className="ccm-cmp-builder__field">
                    <span className="ccm-cmp-builder__label">{t('config_columns_modal.builder_period', 'Período')}</span>
                    <PeriodsSelect name="cmpMain" value={mainPeriod} periods={mainPeriods} onChange={(e) => setMainPeriod(e.target.value)}/>
                </label>
                <label className="ccm-cmp-builder__field">
                    <span className="ccm-cmp-builder__label">{t('config_columns_modal.builder_vs', 'Comparar contra')}</span>
                    <PeriodsSelect name="cmpVs" value={vsPeriod} emptyOptionText={t('config_columns_modal.builder_no_comparison', 'Sin comparación')} onChange={(e) => setVsPeriod(e.target.value)}/>
                </label>
            </div>
            <div className="ccm-cmp-builder__actions">
                <button type="button" className="btn -alt" onClick={onClose} disabled={creating}>
                    {t('common.cancel', 'Cancelar')}
                </button>
                <button type="button" className="btn" onClick={handleCreate} disabled={!mainPeriod || creating}>
                    {creating ? t('common.saving', 'Guardando…') : t('config_columns_modal.builder_create', 'Crear y agregar')}
                </button>
            </div>
        </div>
    );
}

// Disparador del builder. En el límite (12) no abre el builder: muestra tooltip + lleva al editor.
function AddPeriodTrigger({compact, atLimit, onOpen, onGoEditor, t}) {
    if (atLimit) {
        return (
            <TipSpan
                text={t('config_columns_modal.comparative_limit_tooltip', 'Ya tenés 12/12 métricas comparativas (el máximo). Tocá para ir al editor y liberar lugar.')}
                className={`ccm-addperiod-limit ${compact ? '-compact' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onGoEditor(); }}
            >
                <FaIcon iconName="plus"/>
                {!compact && <span>{t('config_columns_modal.add_period', 'Otro período / comparación')}</span>}
            </TipSpan>
        );
    }
    if (compact) {
        return (
            <button
                type="button"
                className="ccm-addperiod -compact"
                onClick={(e) => { e.stopPropagation(); onOpen(); }}
                onMouseDown={(e) => e.stopPropagation()}
                title={t('config_columns_modal.add_period_title', 'Agregar esta métrica en otro período (y, si querés, comparada contra otro)')}
            >
                <FaIcon iconName="plus"/>
            </button>
        );
    }
    return (
        <button type="button" className="ccm-addperiod" onClick={onOpen}>
            <FaIcon iconName="plus"/>
            <span>{t('config_columns_modal.add_period', 'Otro período / comparación')}</span>
        </button>
    );
}

// Row del modal (estilo drawer). Cambios 1, 2 y 6 + ayuda (?) + builder de comparativas.
function DrawerMetricRow({row, activeColumns, onToggleColumn, activeCount, canCreateComparative, onCreateComparative, onClose, comparativeCount, t}) {
    const isIntegrationMissing = !!row.requiresIntegration;
    const hasActive = activeCount > 0;
    const isSingle = row.chips.length === 1;
    const [expanded, setExpanded] = useState(false);
    const [builderOpen, setBuilderOpen] = useState(false);
    const helpText = getColumnHelp(t, row);
    const navigate = useNavigate();

    // Ayuda propia de cada chip (mismo origen que el panel de Columnas seleccionadas).
    // Solo la mostramos cuando aporta algo distinto al helper general de la fila — así una fila
    // como "Ritmo ideal de inversión" muestra la ayuda específica de cada métrica (Desvío vs
    // presupuesto, Desvío vs inv. esperada, Costo diario ideal…) sin repetir el texto de la fila.
    const chipHelpById = useMemo(() => {
        if (isSingle) return {};
        const map = {};
        row.chips.forEach((chip) => {
            const data = getColumnData(t, chip.columnId);
            const h = data && data.helper;
            if (h && h !== helpText) map[chip.columnId] = h;
        });
        return map;
    }, [row, isSingle, helpText, t]);

    const metricName = resolveMetricName(row);
    const hasBuilder = canCreateComparative && !!metricName && !!onCreateComparative;
    const atLimit = (comparativeCount || 0) >= MAX_COMPARATIVES;

    const goEditor = useCallback(() => {
        if (onClose) onClose();
        navigate(COMPARATIVE_EDITOR_PATH);
    }, [navigate, onClose]);

    // Single-chip: fila clickable + (si aplica) "+" inmediatamente después del período.
    if (isSingle) {
        const chip = row.chips[0];
        const isActive = activeColumns.includes(chip.columnId);
        const showPeriod = row.type === ROW_TYPE_FIXED && !!chip.label;
        return (
            <li className={`ccm-stacked-row -single ${isIntegrationMissing ? '-needs-integration' : ''} ${isActive ? '-has-active' : ''}`}>
                <div className="ccm-stacked-row__line">
                    <button
                        type="button"
                        className={`ccm-stacked-row__single-btn ${isActive ? '-active' : ''}`}
                        onClick={() => onToggleColumn(chip.columnId, !isActive)}
                    >
                        <span className="ccm-stacked-row__check-box">
                            {isActive && <FaIcon iconName="check"/>}
                        </span>
                        <span className="ccm-stacked-row__title">{row.titleKey}</span>
                        <InfoHelp text={helpText}/>
                        <RowBadges row={row} t={t}/>
                        {showPeriod && <span className="ccm-stacked-row__period">{chip.label}</span>}
                        {chip.isComparative && (
                            <FaIcon
                                iconName="wand-magic-sparkles"
                                className="ccm-stacked-row__cmp"
                                title={t('config_columns_modal.flex_badge_title', 'Métrica comparativa de la agencia')}
                            />
                        )}
                    </button>
                    {hasBuilder && (
                        <AddPeriodTrigger
                            compact
                            atLimit={atLimit}
                            onOpen={() => setBuilderOpen((v) => !v)}
                            onGoEditor={goEditor}
                            t={t}
                        />
                    )}
                </div>
                {hasBuilder && !atLimit && builderOpen && (
                    <ComparativeBuilder
                        metricName={metricName}
                        count={comparativeCount || 0}
                        onCreate={onCreateComparative}
                        onClose={() => setBuilderOpen(false)}
                        t={t}
                    />
                )}
            </li>
        );
    }

    // Multi-chip: drawer accordion con chevron a la izquierda (cambio 1)
    return (
        <li className={`ccm-drawer-row ${hasActive ? '-has-active' : ''} ${expanded ? '-expanded' : ''} ${isIntegrationMissing ? '-needs-integration' : ''}`}>
            <button
                type="button"
                className="ccm-drawer-row__head"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
            >
                <span className="ccm-drawer-row__lead">
                    <FaIcon iconName="caret-down" className={`ccm-drawer-row__caret ${expanded ? '' : '-collapsed'}`}/>
                </span>
                <span className="ccm-drawer-row__title">{row.titleKey}</span>
                <InfoHelp text={helpText}/>
                <RowBadges row={row} t={t}/>
                <span className="ccm-drawer-row__counter">
                    {activeCount > 0 ? (
                        <span className="ccm-drawer-row__counter-active">{activeCount}</span>
                    ) : null}
                    <span className="ccm-drawer-row__counter-total">/ {row.chips.length}</span>
                </span>
            </button>
            {expanded && (
                <div className="ccm-drawer-row__body">
                    {/* Lista plana (sin labels de canal — el usuario los pidió fuera). Las comparativas
                        van al final (orden de mergeMetricRows) con el ✨. */}
                    <ul className="ccm-drawer-row__opts">
                        {row.chips.map((chip) => {
                            const isActive = activeColumns.includes(chip.columnId);
                            const fullLabel = chip.fullLabel || chip.label;
                            return (
                                <li key={chip.columnId}>
                                    {/* Cambio 2: checkbox uniforme, no pill */}
                                    <button
                                        type="button"
                                        className={`ccm-opt ${isActive ? '-active' : ''} ${chip.isComparative ? '-comparative' : ''}`}
                                        onClick={() => onToggleColumn(chip.columnId, !isActive)}
                                        title={fullLabel}
                                    >
                                        <span className="ccm-opt__check-box">
                                            {isActive && <FaIcon iconName="check"/>}
                                        </span>
                                        <span className="ccm-opt__label">{chip.label}</span>
                                        {chipHelpById[chip.columnId] && <InfoHelp text={chipHelpById[chip.columnId]}/>}
                                        {chip.isComparative && (
                                            <FaIcon
                                                iconName="wand-magic-sparkles"
                                                className="ccm-opt__cmp"
                                                title={t('config_columns_modal.flex_badge_title', 'Métrica comparativa de la agencia')}
                                            />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    {hasBuilder && (
                        <div className="ccm-drawer-row__addperiod">
                            {atLimit ? (
                                <AddPeriodTrigger atLimit onGoEditor={goEditor} t={t}/>
                            ) : builderOpen ? (
                                <ComparativeBuilder
                                    metricName={metricName}
                                    count={comparativeCount || 0}
                                    onCreate={onCreateComparative}
                                    onClose={() => setBuilderOpen(false)}
                                    t={t}
                                />
                            ) : (
                                <AddPeriodTrigger onOpen={() => setBuilderOpen(true)} t={t}/>
                            )}
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}

// Acceso directo al editor de Métricas personalizadas + estado vacío (sin métricas creadas).
function CustomAccess({hasRows, onClose, t}) {
    const navigate = useNavigate();
    const go = () => { if (onClose) onClose(); navigate('/agency/personalization/metrics'); };
    return (
        <div className="ccm-access">
            {hasRows ? (
                <p className="ccm-access__desc">
                    {t('config_columns_modal.custom_desc', 'Métricas creadas por tu agencia combinando otras con una fórmula propia.')}
                </p>
            ) : (
                <p className="ccm-access__empty">
                    {t('config_columns_modal.custom_empty', 'Todavía no creaste métricas personalizadas. Son métricas con tu propia fórmula (combinando las existentes) que después podés sumar como columnas.')}
                </p>
            )}
            {isCustomMetricsEnabled() && (
                <button type="button" className="ccm-access__btn" onClick={go}>
                    <FaIcon iconName="wand-magic-sparkles"/>
                    <span>{t('config_columns_modal.go_to_custom_metrics', 'Ir a Métricas personalizadas')}</span>
                    <FaIcon iconName="external-link" className="ccm-access__ext"/>
                </button>
            )}
        </div>
    );
}

function CategorySection({cat, catRows, activeColumns, onToggleColumn, collapsed, onToggleCat, categoryLabels, headerExtra, canCreateComparative, onCreateComparative, onClose, comparativeCount, t}) {
    const isCollapsed = !!collapsed;
    const activeCount = catRows.reduce((sum, r) => sum + countActiveChips(r, activeColumns), 0);
    return (
        <div className={`ccm-section ${isCollapsed ? '-collapsed' : ''}`}>
            <button type="button" className="ccm-section__header" onClick={onToggleCat}>
                <FaIcon iconName="caret-down" className={`ccm-section__caret ${isCollapsed ? '-collapsed' : ''}`}/>
                <FaIcon iconName={CATEGORY_ICON[cat]} className="ccm-section__icon"/>
                <span className="ccm-section__name">{categoryLabels[cat]}</span>
                {activeCount > 0 && <span className="ccm-section__count">{activeCount}</span>}
            </button>
            {!isCollapsed && (
                <>
                    {headerExtra}
                    {/* Cambio 3: se renderizan TODAS las filas (sin split core/avanzadas ni toggle) */}
                    {catRows.length > 0 && (
                        <ul className="ccm-section__rows">
                            {catRows.map((row) => (
                                <DrawerMetricRow
                                    key={row.id}
                                    row={row}
                                    activeColumns={activeColumns}
                                    onToggleColumn={onToggleColumn}
                                    activeCount={countActiveChips(row, activeColumns)}
                                    canCreateComparative={canCreateComparative}
                                    onCreateComparative={onCreateComparative}
                                    onClose={onClose}
                                    comparativeCount={comparativeCount}
                                    t={t}
                                />
                            ))}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}

export function AvailableColumnsPanel({rows, activeColumns, searchQuery, filters, onToggleColumn, onClose, canCreateComparative, onCreateComparative, comparativeCount, t}) {
    const [collapsed, setCollapsed] = useState({});

    const filteredRows = useMemo(() => {
        return rows.filter((row) => rowMatchesSearch(row, searchQuery) && rowMatchesFilters(row, filters));
    }, [rows, searchQuery, filters]);

    const grouped = useMemo(() => {
        const byCategory = {};
        filteredRows.forEach((row) => {
            const cat = row.category || 'PERFORMANCE';
            if (!byCategory[cat]) byCategory[cat] = [];
            byCategory[cat].push(row);
        });
        // Ordena cada categoría: fijas → destacadas → resto (ver FEATURED_BY_CATEGORY).
        Object.keys(byCategory).forEach((cat) => {
            byCategory[cat] = sortCategoryRows(cat, byCategory[cat]);
        });
        return byCategory;
    }, [filteredRows]);

    const categoryLabels = CATEGORY_LABEL(t);

    const toggleCat = useCallback((cat) => {
        setCollapsed((prev) => ({...prev, [cat]: !prev[cat]}));
    }, []);

    const searchActive = !!(searchQuery || '').trim();
    const customRows = grouped[CATEGORY_CUSTOM] || [];
    const customVisible = (!filters.category || filters.category === CATEGORY_CUSTOM)
        && (!searchActive || customRows.length > 0);

    const nonCustomHasRows = CATEGORY_ORDER_NON_CUSTOM.some((c) => (grouped[c] || []).length > 0);
    const hasAnything = customVisible || nonCustomHasRows;

    const sharedRowProps = {canCreateComparative, onCreateComparative, onClose, comparativeCount};

    return (
        <section className="ccm-available" {...anchorAttr('columns_available')}>
            {!hasAnything && (
                <div className="ccm-available__empty">
                    <FaIcon iconName="search"/>
                    <p>{t('config_columns_modal.no_results', 'Sin resultados con esos filtros')}</p>
                </div>
            )}

            {/* Métricas personalizadas — su propio botón + estado vacío + filas */}
            {customVisible && (
                <CategorySection
                    cat={CATEGORY_CUSTOM}
                    catRows={customRows}
                    activeColumns={activeColumns}
                    onToggleColumn={onToggleColumn}
                    collapsed={collapsed[CATEGORY_CUSTOM]}
                    onToggleCat={() => toggleCat(CATEGORY_CUSTOM)}
                    categoryLabels={categoryLabels}
                    headerExtra={!searchActive ? <CustomAccess hasRows={customRows.length > 0} onClose={onClose} t={t}/> : null}
                    {...sharedRowProps}
                    t={t}
                />
            )}

            {CATEGORY_ORDER_NON_CUSTOM.map((cat) => {
                const catRows = grouped[cat];
                if (!catRows || catRows.length === 0) return null;
                return (
                    <CategorySection
                        key={cat}
                        cat={cat}
                        catRows={catRows}
                        activeColumns={activeColumns}
                        onToggleColumn={onToggleColumn}
                        collapsed={collapsed[cat]}
                        onToggleCat={() => toggleCat(cat)}
                        categoryLabels={categoryLabels}
                        {...sharedRowProps}
                        t={t}
                    />
                );
            })}
        </section>
    );
}

export function SelectedColumnsPanel({activeColumns, onColumnsPositionChange, onColumnRemove, onCopyLink, t}) {
    const dataGetter = useCallback((id) => getColumnData(t, id), [t]);
    // Badge de canal compacto por fila (logo + color), como referencia visual sin agrupar.
    const badgeRenderer = useCallback((id) => <ChannelBadge group={channelGroupForColumn(id)} compact/>, []);

    return (
        <aside className="ccm-selected">
            <header className="ccm-selected__header">
                <h3 className="ccm-selected__title">
                    {t('config_columns_modal.selected_title', 'Columnas seleccionadas')}
                </h3>
                <span className="ccm-selected__count">{activeColumns.length}</span>
                <button
                    type="button"
                    className="ccm-selected__link-btn"
                    onClick={onCopyLink}
                    title={t('config_columns_modal.copy_link_title', 'Copiar link para compartir')}
                >
                    <FaIcon iconName="link"/>
                </button>
            </header>

            {activeColumns.length === 0 ? (
                <div className="ccm-selected__empty">
                    <FaIcon iconName="hand-pointer"/>
                    <p>{t('config_columns_modal.selected_empty', 'Elegí columnas o aplicá una plantilla para empezar')}</p>
                </div>
            ) : (
                // Lista plana única: el orden que ves ES el que se aplica (WYSIWYG). Se arrastra
                // libremente entre cualquier fila, sin que la categoría obligue a agrupar.
                <ActiveColumnsList
                    activeColumns={activeColumns}
                    onColumnsPositionChange={onColumnsPositionChange}
                    getColumnData={dataGetter}
                    onColumnRemove={onColumnRemove}
                    renderBadge={badgeRenderer}
                />
            )}
        </aside>
    );
}
